import axios from "axios";
import { message } from "antd";
import { API_BASE_URL } from "./config";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { COMMON_DATE_FIELDS } from "./apiUtils"; // 导入通用日期字段列表
import { handleVersionHeader } from "../utils/versionCheck"; // 版本检查工具
import { getFreshAccessToken, isAuthRefreshDeterministicError } from "./authRefreshOrchestrator";
import { getAccessToken } from "./authState";
import { getStoredActiveOrganizationId } from "../utils/organizationScope";

dayjs.extend(utc);

// 开发环境标志
const isDev = process.env.NODE_ENV === "development";

// (响应处理器使用的) 定义所有时间相关字段
const RESPONSE_TIME_FIELDS = COMMON_DATE_FIELDS; // 响应处理可以使用通用列表

// (请求处理器使用的) 定义后端期望的日期格式；datetime 字段必须保留 UTC/offset。
const BACKEND_DATE_FORMAT = "YYYY-MM-DD";
const REQUEST_DATE_ONLY_FIELDS = new Set(["flight_date", "schedule_date", "start_date", "end_date"]);
const TIMEZONE_SUFFIX_PATTERN = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

function isDayjsLike(value) {
    return Boolean(value && typeof value === "object" && value.$y !== undefined && value.$M !== undefined && value.$D !== undefined);
}

function formatRequestDateValue(key, value) {
    if (REQUEST_DATE_ONLY_FIELDS.has(key)) {
        const date = dayjs.utc(value);
        return date.isValid() ? date.format(BACKEND_DATE_FORMAT) : value;
    }

    if (typeof value === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return value;
        }
        if (value.includes("T") || value.includes(" ")) {
            if (!TIMEZONE_SUFFIX_PATTERN.test(value.trim())) {
                if (isDev) {
                    console.warn(`请求拦截器: datetime 字段 '${key}' 缺少时区信息，保持原值交由后端拒绝。`);
                }
                return value;
            }
            const date = dayjs(value);
            return date.isValid() ? date.utc().toISOString() : value;
        }
        return value;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? value : value.toISOString();
    }

    if (isDayjsLike(value) || value?._isAMomentObject || value?.$d) {
        const date = dayjs.utc(value);
        return date.isValid() ? date.toISOString() : value;
    }

    return value;
}

/**
 * 递归格式化请求数据中的日期字段 (基于 COMMON_DATE_FIELDS)
 * @param {*} data - 要处理的数据 (对象或数组)
 * @returns {*} 处理后的数据
 */
export const formatRequestDates = (data) => {
    if (!data || typeof data !== "object") {
        return data;
    }

    if (Array.isArray(data)) {
        return data.map(formatRequestDates); // 递归处理数组元素
    }

    const result = { ...data };
    for (const key in result) {
        if (Object.prototype.hasOwnProperty.call(result, key)) {
            const value = result[key];

            if (COMMON_DATE_FIELDS.includes(key) && value) {
                try {
                    result[key] = formatRequestDateValue(key, value);
                } catch (error) {
                    if (isDev) {
                        console.error(`请求拦截器: 格式化字段 '${key}' 出错:`, error);
                    }
                    // 出错时保留原值
                }
            } else if (value && typeof value === "object") {
                // 递归处理嵌套对象或数组
                result[key] = formatRequestDates(value);
            }
            // 如果字段名不在 COMMON_DATE_FIELDS 中，则不处理
        }
    }
    return result;
};

/**
 * 通用时间格式处理工具 - 添加Z后缀（UTC标准格式） - 用于响应
 * @param {Object|Array} obj - 要处理的对象或数组
 * @returns {Object|Array} 处理后的对象或数组
 */
const standardizeTimeFormat = (obj) => {
    if (!obj || typeof obj !== "object") return obj;

    // 处理数组
    if (Array.isArray(obj)) {
        return obj.map((item) => standardizeTimeFormat(item));
    }

    // 处理对象
    const result = { ...obj };

    // 检查并处理所有可能的时间字段
    Object.keys(result).forEach((key) => {
        // 使用 RESPONSE_TIME_FIELDS 进行检查
        if (RESPONSE_TIME_FIELDS.includes(key) && result[key] && typeof result[key] === "string") {
            try {
                const dateValue = result[key];

                // 纯日期字段（YYYY-MM-DD）保持原样，避免被错误转换为前一天的 UTC 时间戳
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                    return;
                }

                // 如果已经是 Z 结尾的 UTC 格式，只需验证和标准化
                if (dateValue.endsWith("Z")) {
                    const parsedDate = dayjs.utc(dateValue);
                    if (parsedDate.isValid()) {
                        result[key] = parsedDate.format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    }
                } else if (TIMEZONE_SUFFIX_PATTERN.test(dateValue.trim())) {
                    // 对于带时区偏移的格式（如 +08:00 或 +00:00），
                    // 使用 dayjs() 先解析识别时区，再转换为 UTC
                    // 这样可以正确处理后端返回的带偏移格式
                    const parsedDate = dayjs(dateValue);
                    if (parsedDate.isValid()) {
                        result[key] = parsedDate.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
                    } else if (isDev) {
                        console.warn(`响应拦截器: 无法将字段 '${key}' 的值 '${dateValue}' 解析为有效日期。`);
                    }
                } else if (isDev) {
                    console.warn(`响应拦截器: datetime 字段 '${key}' 缺少时区信息，保持原值。`);
                }
            } catch (e) {
                if (isDev) {
                    console.error(`响应拦截器: 处理字段 '${key}' 时出错:`, e);
                }
                // 出错时保留原始值
            }
        }
    });

    // 递归处理嵌套对象
    Object.entries(result).forEach(([key, value]) => {
        // 只对非时间字段且是对象的进行递归
        if (!RESPONSE_TIME_FIELDS.includes(key) && value && typeof value === "object") {
            result[key] = standardizeTimeFormat(value);
        }
    });

    return result;
};

/**
 * 通用API请求重试函数
 * @param {Function} apiCall - API调用函数
 * @param {number} maxRetries - 最大重试次数
 * @param {number} delay - 重试延迟(毫秒)
 * @param {Function} shouldRetry - 判断是否应该重试的函数
 * @returns {Promise} API调用的结果
 */
export const retryRequest = async (apiCall, maxRetries = 3, delay = 1000, shouldRetry = () => true) => {
    let lastError = null;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await apiCall();
        } catch (error) {
            lastError = error;

            // 判断是否应该重试
            if (attempt < maxRetries - 1 && shouldRetry(error)) {
                // 计算延迟时间（可以实现指数退避）
                const retryDelay = delay * (attempt + 1);
                if (isDev) {
                    console.log(`请求失败，${retryDelay}ms后重试(${attempt + 1}/${maxRetries})...`, error);
                }
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
            } else {
                break;
            }
        }
    }
    throw lastError;
};

// 创建axios实例
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
    paramsSerializer: {
        indexes: null,
    },
});

// 请求拦截器
apiClient.interceptors.request.use(
    (config) => {
        // 在开发环境下记录请求信息
        if (isDev) {
            const { method, url, params, data } = config;
            console.log(`🚀 API请求: ${method?.toUpperCase()} ${url}`, {
                params,
                data: method !== "get" ? data : undefined,
            });
        }

        // 检查是否需要跳过数据处理（针对FormData或特殊请求）
        if (config._skipInterceptorProcessing) {
            // 记录跳过处理的日志
            if (isDev) {
                console.log(`跳过拦截器处理: ${config?.method?.toUpperCase()} ${config?.url}`);
            }
            // 保留认证信息，但跳过数据处理
            const token = getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }

        // --- 新增：自动格式化请求数据中的日期字段 ---
        // 排除FormData和其他特殊对象类型
        if (config.data && typeof config.data === "object" && !(config.data instanceof FormData)) {
            try {
                config.data = formatRequestDates(config.data);
            } catch (error) {
                if (isDev) {
                    console.error("请求拦截器: 调用 formatRequestDates 出错:", error);
                }
                // 如果格式化失败，可以选择发送原始数据或抛出错误
                // 这里选择发送原始数据，并依赖后端处理或报错
            }
        }
        // --- 结束新增部分 ---

        // ---> 添加日志，检查 removeZSuffix 前的 data (这部分可以移除了，因为不再使用 removeZSuffix)
        // if (config.url === "/v1/import/parse" && ...) { ... }

        // 处理请求数据中的时间格式，移除Z后缀以兼容MySQL (这部分逻辑由 formatRequestDates 替代)
        // if (config.data && typeof config.data === 'object') {
        //     config.data = removeZSuffix(config.data);
        // }

        // 先从localStorage检查，再从sessionStorage检查
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        const activeOrganizationId = getStoredActiveOrganizationId();
        if (activeOrganizationId !== null) {
            config.headers["X-Active-Organization-Id"] = String(activeOrganizationId);
        }

        return config;
    },
    (error) => {
        if (isDev) {
            console.error("❌ 请求配置错误:", error);
        }
        return Promise.reject(error);
    },
);

// 响应拦截器
apiClient.interceptors.response.use(
    (response) => {
        // 在开发环境下记录响应信息
        if (isDev) {
            console.log(`✅ API响应: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
                status: response.status,
                data: response.data,
            });
        }

        // 在此返回，因为认证错误已处理
        if (response.status === 401 || response.status === 403) {
            return Promise.reject(response);
        }

        // 检查是否需要跳过响应数据处理（针对blob或特殊响应）
        // Blob 不能走 standardizeTimeFormat：对其做对象展开会变成普通 {}，
        // 随后 URL.createObjectURL 会报 Overload resolution failed。
        const responseType = response.config?.responseType;
        const isBinaryResponse =
            response.config._skipInterceptorProcessing ||
            responseType === "blob" ||
            responseType === "arraybuffer" ||
            (typeof Blob !== "undefined" && response.data instanceof Blob);
        if (isBinaryResponse) {
            if (isDev) {
                console.log(`跳过响应拦截器处理: ${response.config?.method?.toUpperCase()} ${response.config?.url}`);
            }
            handleVersionHeader(response);
            return response;
        }

        // 统一处理响应数据中的时间格式，确保前端获取到的是带 Z 的 UTC 标准时间
        if (response.data) {
            try {
                response.data = standardizeTimeFormat(response.data);
            } catch (error) {
                if (isDev) {
                    console.error("响应拦截器: 调用 standardizeTimeFormat 出错:", error);
                }
            }
        }

        // 版本检查：对所有 API 检测后端版本变化
        // 如果检测到版本不一致，会提示用户刷新页面
        handleVersionHeader(response);

        return response;
    },
    async (error) => {
        // 全局错误处理
        const { response, config } = error;

        // 在开发环境下记录错误信息
        if (isDev) {
            console.error(`❌ API错误: ${config?.method?.toUpperCase()} ${config?.url}`, error);
        }

        // ── 401 自动续期逻辑（共享 refresh 锁） ──
        // 收到 401 时先尝试 refresh_token 静默续期，而非直接跳登录
        if (response?.status === 401 && config && !config._retry) {
            config._retry = true;
            try {
                const accessToken = await getFreshAccessToken({ entrypoint: "legacy-axios-401", force: true });
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${accessToken}`;
                return apiClient(config);
            } catch (refreshError) {
                if (isAuthRefreshDeterministicError(refreshError)) {
                    message.error("登录已过期，请重新登录");
                }
                return Promise.reject(refreshError);
            }
        }

        // ── 非 401 错误的常规处理 ──
        const shouldShowGlobalError = !config?._silentError;

        if (response && shouldShowGlobalError) {
            const { status, data } = response;

            switch (status) {
                case 403:
                    message.error("无权限访问该资源");
                    break;
                case 404:
                    message.error("请求的资源不存在");
                    break;
                case 500:
                    message.error("服务器错误，请稍后再试");
                    break;
                // 对于400、409、422等业务错误，让组件层处理
                default:
                    if (status >= 500) {
                        const errorMsg = data?.detail || data?.message || `服务器错误: ${status}`;
                        message.error(errorMsg);
                    }
            }
        } else if (!response && shouldShowGlobalError) {
            message.error("网络异常，请检查网络连接");
        }

        return Promise.reject(error);
    },
);

export default apiClient;
